import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canAccessRoute } from "@/lib/roles";
import AuthPage from "@/pages/AuthPage";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Empresa from "@/pages/Empresa";
import Empleados from "@/pages/Empleados";
import Asistencia from "@/pages/Asistencia";
import Planillas from "@/pages/Planillas";
import Rebajos from "@/pages/Rebajos";
import PlanillasMensuales from "@/pages/PlanillasMensuales";
import Vacaciones from "@/pages/Vacaciones";
import Incapacidades from "@/pages/Incapacidades";
import Bonificaciones from "@/pages/Bonificaciones";
import Aguinaldo from "@/pages/Aguinaldo";
import AccionesPersonal from "@/pages/AccionesPersonal";
import AccionesRRHH from "@/pages/AccionesRRHH";
import Solicitudes from "@/pages/Solicitudes";
import Contratos from "@/pages/Contratos";
import Liquidaciones from "@/pages/Liquidaciones";
import Landing from "@/pages/Landing";
import Reportes from "@/pages/Reportes";
import AdminCuentasPendientes from "@/pages/AdminCuentasPendientes";
import PagosCliente from "@/pages/PagosCliente";

const Protected = ({ children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="w-10 h-10 rounded-full border-4 border-[var(--primary)] border-t-transparent spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessRoute(user.role, loc.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <PageErrorBoundary locationKey={loc.key}>{children}</PageErrorBoundary>;
};

// Error boundary a nivel de página para que un error runtime en una página
// (ej: fecha inválida en un cálculo) no deje al usuario con pantalla en blanco.
class PageErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error("Page runtime error:", err, info); }
  componentDidUpdate(prevProps) {
    // Reset del estado de error cuando el usuario navega a otra ruta,
    // así no queda "atrapado" en la pantalla de error al usar el nav.
    if (this.state.err && prevProps.locationKey !== this.props.locationKey) {
      this.setState({ err: null });
    }
  }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-app p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 border border-c text-center">
          <div className="text-4xl mb-2">😕</div>
          <div className="font-display text-xl font-semibold mb-2">Ocurrió un error inesperado</div>
          <div className="text-sm text-secondary-c mb-4">
            La página no se pudo renderizar. Recargá para intentar de nuevo.
          </div>
          <button onClick={() => window.location.reload()} className="np-btn np-btn-primary" data-testid="err-boundary-reload">
            Recargar
          </button>
          <details className="mt-4 text-left text-xs text-secondary-c">
            <summary className="cursor-pointer">Detalles técnicos</summary>
            <pre className="mt-2 p-2 bg-[#FAFAF7] rounded overflow-auto max-h-48">{String(this.state.err && (this.state.err.message || this.state.err))}</pre>
          </details>
        </div>
      </div>
    );
  }
}

function AppRouter() {
  const loc = useLocation();
  // CRITICAL: detect session_id BEFORE routing (Emergent OAuth callback)
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/empresa" element={<Protected><Empresa /></Protected>} />
      <Route path="/empleados" element={<Protected><Empleados /></Protected>} />
      <Route path="/asistencia" element={<Protected><Asistencia /></Protected>} />
      <Route path="/incapacidades" element={<Protected><Incapacidades /></Protected>} />
      <Route path="/planillas" element={<Protected><Planillas /></Protected>} />
      <Route path="/rebajos" element={<Protected><Rebajos /></Protected>} />
      <Route path="/bonificaciones" element={<Protected><Bonificaciones /></Protected>} />
      <Route path="/planillas-mensuales" element={<Protected><PlanillasMensuales /></Protected>} />
      <Route path="/vacaciones" element={<Protected><Vacaciones /></Protected>} />
      <Route path="/aguinaldo" element={<Protected><Aguinaldo /></Protected>} />
      <Route path="/acciones" element={<Protected><AccionesPersonal /></Protected>} />
      <Route path="/documentacion" element={<Protected><AccionesPersonal /></Protected>} />
      <Route path="/acciones-personal" element={<Protected><AccionesRRHH /></Protected>} />
      <Route path="/solicitudes" element={<Protected><Solicitudes /></Protected>} />
      <Route path="/contratos" element={<Protected><Contratos /></Protected>} />
      <Route path="/liquidaciones" element={<Protected><Liquidaciones /></Protected>} />
      <Route path="/reportes" element={<Protected><Reportes /></Protected>} />
      <Route path="/admin/super-administrador" element={<Protected><AdminCuentasPendientes /></Protected>} />
      <Route path="/admin/cuentas-pendientes" element={<Navigate to="/admin/super-administrador" replace />} />
      <Route path="/pagos" element={<Protected><PagosCliente /></Protected>} />
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton toastOptions={{ duration: 4500 }} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
